import { FinanceBalanceState } from './balance/balance.types'
import { InvoiceStatus } from './finance.constants'

export enum InvoiceTypeAlias {
  Utility = 'UTILITY',
  ServiceEstate = 'SERVICE_ESTATE',
  ConsolidatedInvoice = 'CONSOLIDATED_INVOICE',
  RelatedServices = 'RELATED_SERVICES',
  InvoiceForPaymentCom = 'INVOICE_FOR_PAYMENT_COM',
  AdvancePayment = 'ADVANCE_PAYMENT',
  ComplementaryServices = 'COMPLEMENTARY_SERVICES',
  EPD = 'EPD',
  CapitalRepairs = 'CAPITAL_REPAIRS',
  InvoiceForPayment = 'INVOICE_FOR_PAYMENT'
}

export type InvoicesListPayload = {
  projectId: string
  isPaid?: boolean
  query?: string
  periodFrom?: string
  periodTo?: string
  invoiceTypeAlias?: InvoiceTypeAlias[]
  premiseId?: string[]
  customerId?: string[]
  merchantId?: string
  period?: string
  personalAccountId?: string
}

export type BalancesInvoicesPayload = {
  projectId: string
  balanceTypeAlias?: BalanceAlias
  personalAccountId?: string
  periodFrom?: string
  periodTo?: string
  invoiceId?: string
}

export type BalancePayload = {
  projectId: string
  personalAccountId?: string
}

export type BalancesByTypePayload = {
  projectId: string
  personalAccountId?: string
  alias?: string
}

export type ActsListPayload = {
  projectId: string
  query?: string
  periodFrom?: string
  periodTo?: string
  customerId?: string[]
  personalAccountId?: string[]
  contractId?: string[]
}

export type FinanceInvoice = {
  id: string
  name: string
  customerName: string | null
  premiseName: string | null
  canPay: boolean
  debtSum: number
  totalSum: number
  isPaid: boolean
  invoiceType: InvoiceTypeAlias
  status: InvoiceStatus
  invoicePeriod: string
  statusIconUrl?: string
  personalAccountId: string
  iconUrl?: string
}

export type BalanceInvoice = {
  id: string
  iconUrl: string
  premiseId: string
  name: string
  canPay: boolean
  totalSum: number
  paidSum: number
  period: string
  status: InvoiceStatus
}

export enum BalanceAlias {
  Utility = 'UTILITY',
  ComplementaryServices = 'COMPLEMENTARY_SERVICES',
  CapitalRepairs = 'CAPITAL_REPAIRS'
}

export type Balance = {
  alias: BalanceAlias
  name: string
  updatedDate: string
  paidSum: number
  debtSum: number
  initialSum: number
  periodStart: string
  state: FinanceBalanceState
  debtPaidPercent: number
  iconUrl?: string
}

export type DetailedBalanceItem = {
  personalAccountId: string
  totalSum: number
  updatedDate: string
  latestInvoiceFile: InvoiceFile
  paymentStatus: InvoiceStatus
  isCreating: boolean
  premiseName: string
}

export type DetailedBalance = {
  alias: string
  name: string
  updatedDate: string
  totalSum: number
  items: DetailedBalanceItem[]
  isCreating: boolean
}

export type NormalizedInvoiceItem = {
  premiseName: string
  totalSum: number
  name: string
  fileUrl?: string
  fileName?: string
  id?: string
  paymentStatus: InvoiceStatus
  personalAccountId?: string
  isCreating: boolean
}

export type InvoiceFile = {
  name: string
  url: string
}

export type PaymentDetailBalance = {
  alias: BalanceAlias
  name: string
  totalSum: number
  latestInvoiceFile: InvoiceFile
  personalAccountId: string
}

export type PaymentDetailInvoice = {
  id: string
  name: string
  premiseName: string
  totalSum: number
  fileUrl?: string
  fileName?: string
  qrContent?: string
}

export type PersonalAccount = {
  personalAccountId: string
  premiseName: string
  hasDebt: boolean
}

export enum PaySystem {
  Mastercard = 'MASTERCARD',
  Visa = 'VISA',
  Mir = 'MIR',
  Amex = 'AMEX',
  Jcb = 'JCB',
  Cup = 'CUP',
  Acquiring = 'acquring',
  Qr = 'qr'
}

export type PaymentCard = {
  id: string
  number?: string
  paySystem: PaySystem
}

export type AcquiringMethod = {
  merchId: string
  urlDesc: string
  urlOffer: string
  isPermitted: boolean
  cards: PaymentCard[]
}

export type QRMethod = {
  qrContent: string
  isPermitted: boolean
}

export type InvoicePaymentMethods = {
  acquiring?: AcquiringMethod
  qr?: QRMethod
}

export type FinanceInvoiceType = {
  id: string
  name: string
  alias: InvoiceTypeAlias
}

export type BalanceType = {
  name: string
  alias: BalanceAlias
}

export type FinanceInvoicePremise = {
  id: string
  name: string
}

export type FinanceInvoicePremiseResponse = {
  data: FinanceInvoicePremise[]
}

type PaymentDetailError = {
  code: string
  title: string
  message: string
}

export type PaymentDetail = {
  merchId?: string
  totalSum?: number
  balances?: PaymentDetailBalance[]
  invoices?: PaymentDetailInvoice[]
  urlDesc?: string
  urlOffer?: string
  error?: PaymentDetailError
  name?: string
  premiseName?: string
  cards: PaymentCard[]
}

export type FinanceInvoiceCustomer = {
  id: string
  name: string
}

export type FinanceInvoiceCustomerResponse = {
  data: FinanceInvoiceCustomer[]
}

export type FinanceInvoiceFile = {
  url: string
  name: string
}

export enum PaymentClient {
  Mobile = 'MOBILE',
  Web = 'WEB'
}

export enum PaymentActionType {
  Single = 'single',
  Multiply = 'multiply',
  Balance = 'balance'
}

export type FinancePayDTO = {
  merchId: string
  amount: number
  phoneNumber: string | null
  email: string | null
  invoiceIds: string[]
  successReturnUrl: string
  errorReturnUrl: string
  paymentActionType: PaymentActionType.Single | PaymentActionType.Multiply
  cardId?: string
}

export type BalancePayDTO = {
  merchId: string
  amount: number
  email: string | null
  invoiceIds: string[]
  successReturnUrl: string
  errorReturnUrl: string
  paymentActionType: PaymentActionType
  balances?: {
    alias: BalanceAlias
    personalAccountId: string
    amount: number
  }[]
  cardId?: string
}

export type FinancePayResponseType = {
  url: string
  body: string
  contentType: string
}

export type FinanceActFile = {
  name: string
  url: string
}

export type FinanceAct = {
  id: string
  title: string
  file: FinanceActFile
}

export type CustomerFinanceAct = {
  id: string
  personalAccountId: string
  documentNumber: string
  contractNumber: string
  customerName: string
  period: string
  fileName: string
  fileLink: string
}

export type FinanceActCustomer = {
  id: string
  name: string
}

export type CustomersResponse = {
  data: FinanceActCustomer[]
}

export type FinanceActContract = {
  id: string
  name: string
}

export type ContractsResponse = {
  data: FinanceActContract[]
}

export * from './balance/balance.types'
